<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'
import UiNavLink from './UiNavLink.vue'
import UiText from './UiText.vue'
import ResponsiveImage from './ResponsiveImage.vue'
import { isExternalHref } from '../utils/isExternalHref'

export interface UiAvatarProps {
    id?: string
    name?: string
    initials?: string
    size?: 'md' | 'lg' | 'sm' | 'xs' | 'xxs' | 'xxxs' | 'inline'
    url?: string
    file?: File
    responsive?: boolean
    colorCoded?: boolean
    alt?: string
    href?: string
    linkAriaLabel?: string
    imageWidths?: number[]
    imageSizes?: string
    loading?: 'eager' | 'lazy'
    fetchpriority?: 'high' | 'low' | 'auto'
}

const props = withDefaults(defineProps<UiAvatarProps>(), {
    size: 'md',
    responsive: true,
    colorCoded: false,
    imageWidths: () => [400],
    loading: 'lazy',
    fetchpriority: 'auto',
})

const COLOR_PALETTE = [
    'bg-rose-600', 'bg-pink-600', 'bg-fuchsia-600', 'bg-purple-600',
    'bg-violet-600', 'bg-indigo-600', 'bg-blue-600', 'bg-sky-600',
    'bg-cyan-600', 'bg-teal-600', 'bg-emerald-600', 'bg-green-600',
    'bg-amber-600', 'bg-orange-600', 'bg-red-600', 'bg-slate-600',
]

function hashString(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i)
        hash |= 0
    }
    return Math.abs(hash)
}

const fallbackBgClass = computed(() => {
    if (!props.colorCoded) return 'bg-se-dark'
    const seed = props.name || props.initials || ''
    if (!seed) return 'bg-se-dark'
    return COLOR_PALETTE[hashString(seed) % COLOR_PALETTE.length]
})

const objectUrl = ref<string | undefined>(undefined)

const imageSrc = computed(() => {
    if (props.url) return props.url
    if (objectUrl.value) return objectUrl.value
    return undefined
})

const imageLoaded = ref(false)
const imageError = ref(false)
const responsiveImageRef = ref<{ isLoaded: () => boolean } | null>(null)

function onLoad() {
    imageLoaded.value = true
    imageError.value = false
}

function onError() {
    imageLoaded.value = false
    imageError.value = true
}

const shouldShowImage = computed(() => {
    return imageSrc.value && imageLoaded.value && !imageError.value
})

const shouldShowFallback = computed(() => {
    return !shouldShowImage.value && (props.initials || props.name)
})

const externalHref = computed(() => isExternalHref(props.href))
const avatarAlt = computed(() => props.alt || props.name || 'Avatar')
const linkAriaLabel = computed(() => props.linkAriaLabel || (props.name ? `Open ${props.name}` : 'Open profile'))

watch(() => props.file, (newFile) => {
    if (objectUrl.value) {
        URL.revokeObjectURL(objectUrl.value)
        objectUrl.value = undefined
    }
    
    if (newFile && globalThis.URL) {
        objectUrl.value = URL.createObjectURL(newFile)
    }
}, { immediate: true })

watch(imageSrc, () => {
    imageLoaded.value = false
    imageError.value = false
})

onMounted(() => {
    if (responsiveImageRef.value?.isLoaded()) onLoad()
})

onBeforeUnmount(() => {
    if (objectUrl.value) {
        URL.revokeObjectURL(objectUrl.value)
        objectUrl.value = undefined
    }
})

const textSize = computed(() => {
    switch (props.size) {
        case 'lg':
            return 'h3'
        case 'md':
            return 'h4'
        case 'sm':
            return 'h5'
        case 'xs':
            return 'h6'
        case 'xxs':
            return 'caption'
        case 'xxxs':
            return 'caption'
        case 'inline':
            return 'override'
    }
})

const displayInitials = computed(() => {
    if (props.initials) {
        const cleaned = props.initials.trim()
        if (props.size === 'inline') return cleaned.charAt(0).toUpperCase()
        if (cleaned.length <= 2) return cleaned.toUpperCase()
        return (cleaned[0] + cleaned[cleaned.length - 1]).toUpperCase()
    }
    if (props.name) {
        const words = props.name.trim().split(/\s+/)
        if (props.size === 'inline') return words[0]?.charAt(0).toUpperCase() ?? ''
        return words.slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('')
    }
    return ''
})
</script>

<template>
    <component
        :is="props.href ? UiNavLink : 'div'"
        :id="props.id" 
        :to="props.href && !externalHref ? props.href : undefined"
        :href="props.href"
        :external="externalHref || undefined"
        :aria-label="props.href ? linkAriaLabel : undefined"
        class="relative overflow-hidden rounded-full flex items-center justify-center shrink-0"
        :class="[
            {
                'md:w-30 md:h-30 w-25 h-25': props.size === 'lg' && props.responsive,
                'md:w-20 md:h-20 w-20 h-20': props.size === 'md' && props.responsive,
                'md:w-16 md:h-16 w-12 h-12': props.size === 'sm' && props.responsive,
                'md:w-12 md:h-12 w-10 h-10': props.size === 'xs' && props.responsive,
                'w-30 h-30': props.size === 'lg' && !props.responsive,
                'w-20 h-20': props.size === 'md' && !props.responsive,
                'w-16 h-16': props.size === 'sm' && !props.responsive,
                'w-12 h-12': props.size === 'xs' && !props.responsive,
                'w-8 h-8': props.size === 'xxs',
                'w-6 h-6': props.size === 'xxxs',
                'w-4 h-4': props.size === 'inline',
            },
            props.href ? 'se-transition-all hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark' : '',
        ]"
    >
        <ResponsiveImage
            v-if="imageSrc"
            ref="responsiveImageRef"
            :src="imageSrc"
            :alt="avatarAlt"
            :widths="props.imageWidths"
            :sizes="props.imageSizes"
            :loading="props.loading"
            :fetchpriority="props.fetchpriority"
            class="absolute inset-0 w-full h-full object-cover"
            :class="{ 'opacity-0': !shouldShowImage, 'opacity-100': shouldShowImage }"
            @load="onLoad"
            @error="onError"
        />
        
        <div 
            v-if="shouldShowFallback"
            class="absolute inset-0 w-full h-full flex items-center justify-center text-white"
            :class="fallbackBgClass"
        >
            <UiText
                as="span"
                :variant="textSize"
                class="font-semibold select-none"
                :class="{ 'text-[9px] leading-none': props.size === 'inline' }"
            >
                {{ displayInitials }}
            </UiText>
        </div>
    </component>
</template>
